import { useCallback, useEffect, useRef, useState } from 'react'
import type { BulkMediaApi } from './api'
import { contentTypeOf, isPhotoOrVideo, MAX_FILE_BYTES, stageFile } from './stage'

export type StagedStatus = 'queued' | 'staging' | 'ready' | 'failed'

export interface StagedEntry {
  key: string
  file: File
  name: string
  size: number
  kind: 'photo' | 'video'
  status: StagedStatus
  /** 0..1 while staging. */
  progress: number
  fileId?: string
  error?: string
  /** Object URL for a photo's thumbnail. */
  preview?: string
}

/** Files sent to the console at once. */
const PARALLEL_FILES = 2

let seq = 0

/**
 * The files picked for a job, each staged on the console as soon as it is added
 * (two at a time). Removing a file stops its upload and discards the staged copy.
 */
export function useStagedFiles(api: BulkMediaApi, { limit }: { limit: number }) {
  const [entries, setEntries] = useState<StagedEntry[]>([])
  const live = useRef<StagedEntry[]>([])
  live.current = entries
  const controllers = useRef(new Map<string, AbortController>())
  const running = useRef(0)

  const patch = useCallback((key: string, change: Partial<StagedEntry>) => {
    setEntries((list) => list.map((e) => (e.key === key ? { ...e, ...change } : e)))
  }, [])

  const pump = useCallback(() => {
    while (running.current < PARALLEL_FILES) {
      const next = live.current.find((e) => e.status === 'queued' && !controllers.current.has(e.key))
      if (!next) return
      const ctrl = new AbortController()
      controllers.current.set(next.key, ctrl)
      running.current++
      // Mark it here too: the state update lands after this loop looks again.
      live.current = live.current.map((e) => (e.key === next.key ? { ...e, status: 'staging' } : e))
      patch(next.key, { status: 'staging', progress: 0, error: undefined })
      stageFile(api, next.file, { signal: ctrl.signal, onProgress: (progress) => patch(next.key, { progress }) })
        .then((fileId) => patch(next.key, { status: 'ready', fileId, progress: 1 }))
        .catch((e: unknown) => {
          if (ctrl.signal.aborted) return
          patch(next.key, { status: 'failed', error: e instanceof Error ? e.message : String(e) })
        })
        .finally(() => {
          controllers.current.delete(next.key)
          running.current--
          pump()
        })
    }
  }, [api, patch])

  useEffect(() => {
    pump()
  }, [entries, pump])

  /** Add picked files; returns what was refused and why. */
  const add = useCallback(
    (files: File[]) => {
      const refused: { name: string; reason: 'type' | 'size' | 'limit' }[] = []
      const room = Math.max(0, limit - live.current.length)
      const next: StagedEntry[] = []
      for (const file of files) {
        if (!isPhotoOrVideo(file)) refused.push({ name: file.name, reason: 'type' })
        else if (file.size > MAX_FILE_BYTES) refused.push({ name: file.name, reason: 'size' })
        else if (next.length >= room) refused.push({ name: file.name, reason: 'limit' })
        else {
          const kind = contentTypeOf(file).startsWith('video/') ? 'video' : 'photo'
          next.push({
            key: `f${++seq}`,
            file,
            name: file.name,
            size: file.size,
            kind,
            status: 'queued',
            progress: 0,
            preview: kind === 'photo' && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : undefined,
          })
        }
      }
      if (next.length) setEntries((list) => [...list, ...next])
      return refused
    },
    [limit],
  )

  const drop = useCallback(
    (key: string) => {
      const entry = live.current.find((e) => e.key === key)
      if (!entry) return
      controllers.current.get(key)?.abort()
      if (entry.fileId) void api.discardFile(entry.fileId).catch(() => undefined)
      if (entry.preview) URL.revokeObjectURL(entry.preview)
      setEntries((list) => list.filter((e) => e.key !== key))
    },
    [api],
  )

  const retry = useCallback((key: string) => patch(key, { status: 'queued', progress: 0, error: undefined }), [patch])

  /** Forget every file (after a job took them): the job owns the staged copies now. */
  const reset = useCallback(() => {
    for (const e of live.current) if (e.preview) URL.revokeObjectURL(e.preview)
    setEntries([])
  }, [])

  useEffect(
    () => () => {
      for (const c of controllers.current.values()) c.abort()
    },
    [],
  )

  const busy = entries.some((e) => e.status === 'queued' || e.status === 'staging')
  return { entries, add, drop, retry, reset, busy }
}
